/**
 * Geocoding service for reverse geocoding (coordinates to addresses)
 * Uses Firebase Cloud Function HTTP endpoint to avoid API key restriction issues
 * No native module required - uses standard HTTP requests
 */

import {auth} from '../config/firebase';

interface GeocodingResult {
  street: string;
  crossStreet: string | null;
  fullAddress: string;
}

/**
 * Get the Cloud Function URL for reverse geocoding
 * This will be set after the function is deployed
 */
const getReverseGeocodeUrl = (): string => {
  // Get the function URL from Firebase project
  // Format: https://{region}-{project-id}.cloudfunctions.net/reverseGeocodeHttp
  // For us-central1: https://us-central1-respondermap.cloudfunctions.net/reverseGeocodeHttp
  return 'https://us-central1-respondermap.cloudfunctions.net/reverseGeocodeHttp';
};

/**
 * Reverse geocode coordinates using Firebase Cloud Function HTTP endpoint
 * This avoids API key restriction issues by calling from the server
 * @param latitude - Latitude coordinate
 * @param longitude - Longitude coordinate
 * @returns Promise with street and cross street information
 */
/**
 * Internal: single attempt at calling the reverseGeocodeHttp Cloud Function.
 * Throws on timeout, network error, non-2xx status, or malformed response so
 * the caller can decide whether to retry or fall back.
 */
const attemptReverseGeocode = async (
  latitude: number,
  longitude: number,
  timeoutMs: number,
): Promise<GeocodingResult> => {
  let idToken: string | null = null;
  try {
    const currentUser = auth().currentUser;
    if (currentUser) {
      idToken = await currentUser.getIdToken();
    }
  } catch (authError) {
    console.warn('Could not get auth token:', authError);
  }

  // Explicit timeout via AbortController so we don't wait for iOS's 60s
  // NSURLSession default when the network stack stalls (common on cold launch
  // immediately after a deep link, when WiFi/auth aren't fully ready).
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const functionUrl = getReverseGeocodeUrl();
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken && {Authorization: `Bearer ${idToken}`}),
      },
      body: JSON.stringify({latitude, longitude}),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      street: data.street || `Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
      crossStreet: data.crossStreet || null,
      fullAddress: data.fullAddress || `${latitude}, ${longitude}`,
    };
  } finally {
    clearTimeout(timeoutId);
  }
};

export const reverseGeocode = async (
  latitude: number,
  longitude: number,
): Promise<GeocodingResult> => {
  console.log('Calling Cloud Function for geocoding:', latitude, longitude);

  try {
    const result = await attemptReverseGeocode(latitude, longitude, 10000);
    console.log('Geocoding successful via Cloud Function:', result);
    return result;
  } catch (firstError: any) {
    console.warn(
      'Reverse geocode first attempt failed, retrying in 1.5s:',
      firstError?.message || firstError,
    );

    // Wait for the network/auth stack to settle before retrying. Cold-launch
    // fetch failures typically recover by this point.
    await new Promise(resolve => setTimeout(resolve, 1500));

    try {
      const result = await attemptReverseGeocode(latitude, longitude, 10000);
      console.log('Geocoding successful on retry:', result);
      return result;
    } catch (secondError: any) {
      console.error('Error in reverse geocoding (after retry):', secondError);
      console.error('Error details:', {
        message: secondError?.message,
        code: secondError?.code,
        stack: secondError?.stack,
      });

      // Fallback: Return coordinates if both attempts fail. This allows the
      // app to continue functioning, but callers that rely on a real address
      // (e.g. the navigation deep-link handler) will see a labelless result.
      const fallbackStreet = `Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`;
      return {
        street: fallbackStreet,
        crossStreet: null,
        fullAddress: `${latitude}, ${longitude}`,
      };
    }
  }
};

/**
 * Get street and nearest cross street from coordinates
 * This is a simplified version - for production, you might want to use
 * a more sophisticated approach with intersection detection
 */
export const getStreetInfo = async (
  latitude: number,
  longitude: number,
): Promise<{street: string; crossStreet: string | null}> => {
  try {
    const result = await reverseGeocode(latitude, longitude);
    
    // If we don't have a cross street, try to find nearby intersections
    // This would require additional API calls or a more sophisticated service
    // For now, we'll return what we have
    
    return {
      street: result.street,
      crossStreet: result.crossStreet,
    };
  } catch (error: any) {
    console.error('Error in getStreetInfo:', error);
    // Return fallback values
    return {
      street: `Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`,
      crossStreet: null,
    };
  }
};

interface ForwardGeocodeResult {
  latitude: number;
  longitude: number;
  formattedAddress: string;
}

/**
 * Internal: single attempt at calling the forwardGeocodeHttp Cloud Function.
 * Throws on timeout, network error, non-2xx status, or malformed response so
 * the caller can decide whether to retry or fall back. Mirrors the shape of
 * attemptReverseGeocode so the retry semantics stay consistent.
 */
const attemptForwardGeocode = async (
  address: string,
  timeoutMs: number,
): Promise<ForwardGeocodeResult> => {
  let idToken: string | null = null;
  try {
    const currentUser = auth().currentUser;
    if (currentUser) {
      idToken = await currentUser.getIdToken();
    }
  } catch (authError) {
    console.warn('Could not get auth token:', authError);
  }

  // Explicit timeout via AbortController so we don't wait for iOS's 60s
  // NSURLSession default when the network stack stalls (common right after
  // a deep link / tab switch, before WiFi+auth fully settle).
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const functionUrl = 'https://us-central1-respondermap.cloudfunctions.net/forwardGeocodeHttp';
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken && {Authorization: `Bearer ${idToken}`}),
      },
      body: JSON.stringify({address}),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (
      typeof data.latitude !== 'number' ||
      typeof data.longitude !== 'number' ||
      isNaN(data.latitude) ||
      isNaN(data.longitude)
    ) {
      throw new Error('Forward geocode returned invalid coordinates');
    }

    return {
      latitude: data.latitude,
      longitude: data.longitude,
      formattedAddress: data.formattedAddress || address,
    };
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Forward geocode an address string to coordinates using the Cloud Function
 * Used by the navigation intent handler for address-only deep links
 * @param address - The address string to geocode
 * @returns Promise with coordinates and formatted address, or null on failure
 */
export const forwardGeocode = async (
  address: string,
): Promise<ForwardGeocodeResult | null> => {
  if (!address || address.trim().length === 0) {
    return null;
  }

  console.log('🧭 Forward geocoding address:', address);

  try {
    const result = await attemptForwardGeocode(address, 10000);
    console.log('🧭 Forward geocode successful:', result);
    return result;
  } catch (firstError: any) {
    console.warn(
      '🧭 Forward geocode first attempt failed, retrying in 1.5s:',
      firstError?.message || firstError,
    );

    // Wait for the network/auth stack to settle before retrying. On iOS, cold
    // or tab-switch fetch failures typically recover by this point. On Android
    // the first attempt almost always succeeds, so this retry path is dormant
    // and Android behavior stays identical.
    await new Promise(resolve => setTimeout(resolve, 1500));

    try {
      const result = await attemptForwardGeocode(address, 10000);
      console.log('🧭 Forward geocode successful on retry:', result);
      return result;
    } catch (secondError: any) {
      console.error(
        '🧭 Forward geocoding error (after retry):',
        secondError?.message || secondError,
      );
      return null;
    }
  }
};

/**
 * Get route directions with snapped coordinates (following roads)
 * Uses Firebase Cloud Function HTTP endpoint
 * @param waypoints - Array of waypoints with latitude and longitude
 * @returns Promise with snapped route coordinates and encoded polyline
 */
export const getRouteDirections = async (
  waypoints: Array<{latitude: number; longitude: number}>,
): Promise<{coordinates: Array<{latitude: number; longitude: number}>; encodedPolyline?: string}> => {
  try {
    console.log('🛣️ Getting route directions for', waypoints.length, 'waypoints');
    console.log('🛣️ Waypoints:', waypoints.map(wp => `${wp.latitude},${wp.longitude}`).join(' -> '));
    
    // Get auth token if user is logged in
    let idToken: string | null = null;
    try {
      const currentUser = auth().currentUser;
      if (currentUser) {
        idToken = await currentUser.getIdToken();
      }
    } catch (authError) {
      console.warn('Could not get auth token:', authError);
    }
    
    // Call the Cloud Function via HTTP
    const functionUrl = 'https://us-central1-respondermap.cloudfunctions.net/getRouteDirections';
    console.log('🛣️ Calling Cloud Function:', functionUrl);
    
    const requestBody = {waypoints};
    console.log('🛣️ Request body:', JSON.stringify(requestBody, null, 2));
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken && {Authorization: `Bearer ${idToken}`}),
      },
      body: JSON.stringify(requestBody),
    });
    
    console.log('🛣️ Response status:', response.status, response.statusText);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('🛣️ Route directions HTTP error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
      });
      
      // If 404, the function might not be deployed yet - return original waypoints
      if (response.status === 404) {
        console.warn('🛣️ Route directions function not found (404) - function may not be deployed yet');
        return {
          coordinates: waypoints,
        };
      }
      throw new Error(`HTTP error: ${response.status} ${errorData.error || response.statusText}`);
    }
    
    const data = await response.json();
    console.log('🛣️ Route directions response:', {
      success: data.success,
      hasCoordinates: !!data.coordinates,
      coordinatesCount: data.coordinates?.length || 0,
      hasEncodedPolyline: !!data.encodedPolyline,
    });
    
    if (!data.success || !data.coordinates) {
      console.warn('🛣️ Route directions returned invalid data, using original waypoints');
      return {
        coordinates: waypoints,
      };
    }
    
    console.log('🛣️ Route directions successful:', {
      originalWaypoints: waypoints.length,
      snappedPoints: data.coordinates.length,
    });
    
    return {
      coordinates: data.coordinates,
      encodedPolyline: data.encodedPolyline,
    };
  } catch (error: any) {
    console.error('🛣️ Error getting route directions (will use original waypoints):', {
      message: error.message,
      error: error,
    });
    // Fallback: return original waypoints if snapping fails
    // This is expected if Directions API isn't enabled or function has issues
    return {
      coordinates: waypoints,
    };
  }
};

